import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Chatbot.css';

// ===== FUNCIONES DE AYUDA MEJORADAS =====
const getAuthToken = () => {
  try {
    // Intentar obtener token de localStorage (si fue guardado previamente)
    const storedToken = localStorage.getItem('auth_token_jwt');
    if (storedToken && storedToken.split('.').length === 3) {
      return storedToken;
    }
    
    // Intentar obtener de cookies (solo si no es HttpOnly)
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.includes('=')) {
        const [name, value] = cookie.split('=');
        const decodedValue = decodeURIComponent(value);
        
        // Un token JWT tiene 3 partes separadas por puntos
        if (decodedValue.split('.').length === 3) {
          localStorage.setItem('auth_token_jwt', decodedValue);
          return decodedValue;
        }
      }
    }
    
    // Si no encontramos token explícito, retornar null
    // Las cookies HttpOnly se enviarán automáticamente con credentials: 'include'
    return null;
  } catch (error) {
    console.error('❌ Error obteniendo token:', error);
    return null;
  }
};

const authenticatedFetch = async (url, options = {}) => {
  // Intentar obtener token explícito (opcional, las cookies HttpOnly funcionan automáticamente)
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Agregar Authorization header si hay token explícito disponible
  // Si no hay token, las cookies HttpOnly se enviarán automáticamente con credentials: 'include'
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include' // Importante: incluir cookies automáticamente
    });
    
    return response;
  } catch (error) {
    console.error('❌ Error en fetch:', error);
    throw error;
  }
};

const fetchAuthTokenFromServer = async () => {
  try {
    const response = await fetch('/api/auth/check', {
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      
      // Intentar obtener token de diferentes campos posibles en la respuesta
      const token = data.user?.token || 
                   data.token_info?.token || 
                   data.token ||
                   data.jwt;
      
      if (token && token.split('.').length === 3) {
        localStorage.setItem('auth_token_jwt', token);
        return token;
      }
    }
    
    // Si no hay token explícito, no es problema
    // Las cookies HttpOnly funcionan automáticamente
    return null;
  } catch (error) {
    console.error('❌ Error obteniendo token del servidor:', error);
    return null;
  }
};

// ===== COMPONENTE CHATBOT =====
const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [currentSession, setCurrentSession] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [hasUnread, setHasUnread] = useState(false);
    const [showSessionsMenu, setShowSessionsMenu] = useState(false);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [activeSessionInfo, setActiveSessionInfo] = useState(null);
    const [showDebug, setShowDebug] = useState(localStorage.getItem('debugHidden') !== 'true');
    const [hasLoadedSessions, setHasLoadedSessions] = useState(false);
    const [showNewSessionModal, setShowNewSessionModal] = useState(false);
    const [newSessionTitle, setNewSessionTitle] = useState('');
    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [showMessageActions, setShowMessageActions] = useState(null); // ID del mensaje con acciones visibles
    
    const ws = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const sessionsMenuRef = useRef(null);
    const debugPanelRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const newSessionInputRef = useRef(null);

    // Cerrar menú de sesiones al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sessionsMenuRef.current && !sessionsMenuRef.current.contains(event.target)) {
                setShowSessionsMenu(false);
            }
        };

        if (showSessionsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSessionsMenu]);

    // Focus en input de nuevo título cuando se abre el modal
    useEffect(() => {
        if (showNewSessionModal && newSessionInputRef.current) {
            newSessionInputRef.current.focus();
        }
    }, [showNewSessionModal]);

    // Función para limpiar WebSocket
    const cleanupWebSocket = useCallback(() => {
        console.log('🧹 [cleanupWebSocket] Limpiando...');
        
        // Cancelar reconexión
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        
        // Cerrar WebSocket
        if (ws.current) {
            ws.current.onopen = null;
            ws.current.onmessage = null;
            ws.current.onerror = null;
            ws.current.onclose = null;
            
            if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                ws.current.close(1000, 'Limpieza');
            }
            ws.current = null;
        }
        
        setIsConnected(false);
    }, []);

    // WebSocket connection - CORREGIDO para usar proxy de Vite
    const connectWebSocket = useCallback((sessionUuid) => {
        if (!sessionUuid) {
            console.log('❌ No hay sessionUuid para conectar WebSocket');
            return;
        }
        
        console.log('🔗 [connectWebSocket] Conectando WebSocket para sesión (UUID):', sessionUuid);

        // 1. Cancelar cualquier reconexión pendiente
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
            console.log('⏹️ Cancelada reconexión pendiente');
        }

        // 2. Cerrar WebSocket actual si existe
        if (ws.current) {
            console.log('🔌 Cerrando WebSocket anterior...');
            ws.current.onopen = null;
            ws.current.onmessage = null;
            ws.current.onerror = null;
            ws.current.onclose = null;
            
            if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                ws.current.close(1000, 'Cambio de sesión');
            }
            ws.current = null;
        }

        // Intentar obtener token explícito (opcional)
        // Las cookies HttpOnly se enviarán automáticamente con WebSocket desde el mismo origen
        const token = getAuthToken();
        
        // ✅ CORRECCIÓN CRÍTICA: Usar ruta relativa para que Vite la proxie
        let wsUrl = `/ws/chat?session_id=${sessionUuid}`;
        
        // Agregar token como query param solo si está disponible explícitamente
        // Si no, el backend usará la cookie auth_token automáticamente
        if (token) {
            wsUrl += `&token=${encodeURIComponent(token)}`;
        }
        
        console.log('🔗 Conectando WebSocket:', {
            url: wsUrl,
            session_id: sessionUuid,
            has_explicit_token: !!token,
            note: token ? 'Usando token explícito' : 'Usando cookies HttpOnly automáticamente'
        });
        
        try {
            ws.current = new WebSocket(wsUrl);
            
            ws.current.onopen = () => {
                console.log('✅ [WebSocket onopen] CONECTADO para sesión:', sessionUuid);
                setIsConnected(true);
                
                // Solo cargar historial si estamos en la sesión correcta
                if (currentSession === sessionUuid) {
                    console.log('📜 Cargando historial para sesión activa (UUID):', sessionUuid);
                    loadChatHistory(sessionUuid);
                }
            };
            
            ws.current.onmessage = (event) => {
                // Verificar que todavía estamos en la misma sesión
                if (currentSession !== sessionUuid) {
                    console.warn('⚠️ Mensaje recibido para sesión incorrecta, ignorando');
                    return;
                }
                
                try {
                    const data = JSON.parse(event.data);
                    console.log('📨 [WebSocket onmessage] Datos recibidos:', data);
                    handleWebSocketMessage(data);
                } catch (error) {
                    console.error('❌ Error parsing WebSocket message:', error, event.data);
                }
            };
            
            ws.current.onerror = (error) => {
                console.error('❌ WebSocket error:', error);
                if (currentSession === sessionUuid) {
                    setIsConnected(false);
                }
            };
            
            ws.current.onclose = (event) => {
                console.log('🔌 WebSocket DESCONECTADO para sesión (UUID):', sessionUuid, 
                           'Código:', event.code, 'Razón:', event.reason);
                
                if (currentSession === sessionUuid) {
                    setIsConnected(false);
                    
                    if (isOpen && event.code !== 1000 && event.code !== 1005) {
                        console.log('🔄 Programando reconexión en 3 segundos...');
                        reconnectTimeoutRef.current = setTimeout(() => {
                            if (currentSession === sessionUuid) {
                                console.log('🔄 Reconectando WebSocket...');
                                connectWebSocket(sessionUuid);
                            }
                        }, 3000);
                    }
                }
            };
            
        } catch (error) {
            console.error('❌ Error creating WebSocket:', error);
            if (currentSession === sessionUuid) {
                setIsConnected(false);
            }
        }
    }, [isOpen, currentSession]);

    // Handle WebSocket messages - CORREGIDO para eliminar case duplicado
    const handleWebSocketMessage = useCallback((data) => {
        console.log('📨 [handleWebSocketMessage] Tipo:', data.type, 'Payload:', data.payload);
        
        switch (data.type) {
            case 'connected':
                console.log('✅ Connected to chat session:', data.payload);
                setIsConnected(true);
                break;
                
            case 'user_message':
                console.log('✅ [handleWebSocketMessage] Mensaje de usuario confirmado:', data.payload);
                
                // El payload tiene { message: {...}, session_id: "...", source: "..." }
                const userMessageData = data.payload.message || data.payload;
                if (!userMessageData) {
                    console.error('❌ No hay datos de mensaje en user_message');
                    break;
                }
                
                const realMessageUUID = userMessageData.message_uuid || userMessageData.id || userMessageData.message_uuid;
                
                console.log('📦 Detalles del mensaje de usuario:', {
                    uuid: realMessageUUID,
                    content: userMessageData.content?.substring(0, 50),
                    role: userMessageData.role
                });
                
                // Buscar y actualizar el mensaje temporal más reciente del usuario
                setMessages(prev => {
                    const newMessages = [...prev];
                    let updated = false;
                    
                    // Buscar desde el final hacia atrás para encontrar el último mensaje temporal
                    for (let i = newMessages.length - 1; i >= 0; i--) {
                        const msg = newMessages[i];
                        
                        // Buscar mensaje de usuario en estado 'sending' o que empiece con 'temp_'
                        if (msg.role === 'user' && (msg.status === 'sending' || msg.id.startsWith('temp_'))) {
                            // Verificar que el contenido coincida (para evitar actualizar el mensaje incorrecto)
                            if (!userMessageData.content || msg.content === userMessageData.content || 
                                userMessageData.content.includes(msg.content.substring(0, 20))) {
                                console.log(`✅ Actualizando mensaje temporal: ${msg.id} → ${realMessageUUID}`);
                                
                                newMessages[i] = {
                                    ...msg,
                                    id: realMessageUUID || msg.id,
                                    status: 'sent',
                                    timestamp: userMessageData.sent_at 
                                        ? new Date(userMessageData.sent_at) 
                                        : new Date(userMessageData.timestamp * 1000 || Date.now())
                                };
                                updated = true;
                                break;
                            }
                        }
                    }
                    
                    if (!updated) {
                        console.log('⚠️ No se encontró mensaje temporal, agregando nuevo');
                        newMessages.push({
                            id: realMessageUUID || `user_${Date.now()}`,
                            role: 'user',
                            content: userMessageData.content,
                            timestamp: userMessageData.sent_at 
                                ? new Date(userMessageData.sent_at) 
                                : new Date(userMessageData.timestamp * 1000 || Date.now()),
                            status: 'sent'
                        });
                    }
                    
                    return newMessages;
                });
                break;
                
            case 'message_sent':
                console.log('✅ [handleWebSocketMessage] Message sent confirmation:', data.payload);
                
                // El backend puede enviar message_id o message_uuid
                const backendMessageId = data.payload.message_uuid || data.payload.message_id;
                
                if (!backendMessageId) {
                    console.warn('⚠️ message_sent sin message_id ni message_uuid');
                    break;
                }
                
                // Buscar el mensaje temporal más reciente del usuario que esté en estado 'sending'
                setMessages(prev => {
                    const newMessages = [...prev];
                    let updated = false;
                    
                    // Buscar desde el final hacia atrás para encontrar el último mensaje temporal
                    for (let i = newMessages.length - 1; i >= 0; i--) {
                        const msg = newMessages[i];
                        
                        // Buscar mensaje de usuario en estado 'sending' o que empiece con 'temp_'
                        if (msg.role === 'user' && (msg.status === 'sending' || msg.id.startsWith('temp_'))) {
                            console.log(`✅ Actualizando mensaje temporal: ${msg.id} → ${backendMessageId}`);
                            newMessages[i] = {
                                ...msg,
                                id: backendMessageId,
                                status: 'sent',
                                timestamp: data.payload.sent_at 
                                    ? new Date(data.payload.sent_at * 1000) 
                                    : new Date(data.payload.timestamp * 1000 || Date.now())
                            };
                            updated = true;
                            break;
                        }
                    }
                    
                    if (!updated) {
                        console.warn('⚠️ No se encontró mensaje temporal para actualizar');
                    }
                    
                    return newMessages;
                });
                break;
                
            case 'typing':
                console.log('⌨️ Assistant is typing...');
                setIsTyping(true);
                break;
                
            case 'chat_response':
            case 'new_chat_message':
                console.log('📥 New chat message:', data.payload);
                setIsTyping(false);
                
                const messageData = data.payload.message || data.payload;
                addMessage({
                    id: messageData.message_uuid || messageData.id || `msg_${Date.now()}`,
                    role: 'assistant',
                    content: typeof messageData.content === 'object' 
                        ? JSON.stringify(messageData.content, null, 2)
                        : messageData.content,
                    timestamp: new Date(messageData.sent_at || messageData.timestamp || Date.now()),
                    metadata: messageData.metadata
                });
                setHasUnread(!isOpen);
                break;
                
            case 'error':
                console.error('❌ WebSocket error:', data.payload);
                addMessage({
                    id: `error_${Date.now()}`,
                    role: 'system',
                    content: `Error: ${data.payload.error || data.payload.message || 'Unknown error'}`,
                    timestamp: new Date(),
                    isError: true
                });
                setIsTyping(false);
                break;
                
            case 'ping':
                if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                    ws.current.send(JSON.stringify({
                        type: 'pong',
                        timestamp: Date.now()
                    }));
                }
                break;
                
            default:
                console.log('ℹ️ Unknown message type:', data.type, data);
        }
    }, [isOpen]);

    // Add message to chat - SOLUCIÓN: Actualizado para manejar mejor los mensajes temporales
    const addMessage = (message) => {
        console.log('🆕 [addMessage] Agregando mensaje:', message.id, message.role, message.content.substring(0, 50));
        
        setMessages(prev => {
            // Verificar si ya existe un mensaje con el mismo ID
            const existingIndex = prev.findIndex(msg => msg.id === message.id);
            
            if (existingIndex >= 0) {
                console.log('🔄 [addMessage] Mensaje ya existe, actualizando:', message.id);
                // Reemplazar el mensaje existente manteniendo el orden
                const newMessages = [...prev];
                newMessages[existingIndex] = {
                    ...newMessages[existingIndex],
                    ...message
                };
                return newMessages;
            } else {
                console.log('➕ [addMessage] Mensaje nuevo agregado:', message.id);
                // Agregar nuevo mensaje al final
                return [...prev, message];
            }
        });
    };

    // Send message - CORREGIDO para agregar el mensaje del usuario inmediatamente
    const sendMessage = () => {
        if (!inputMessage.trim() || !ws.current || ws.current.readyState !== WebSocket.OPEN) {
            console.error('❌ Cannot send message:', {
                hasInput: !!inputMessage.trim(),
                wsReady: ws.current?.readyState,
                wsOpen: ws.current?.readyState === WebSocket.OPEN
            });
            return;
        }

        const message = inputMessage.trim();
        const messageId = `temp_${Date.now()}`;
        const sessionIdentifier = activeSessionInfo?.session_uuid || currentSession;
        
        console.log('📤 [sendMessage] Enviando mensaje:', {
            messageId,
            content: message,
            session: sessionIdentifier
        });
        
        if (!sessionIdentifier) {
            console.error('❌ No hay identificador de sesión válido (session_uuid)');
            addMessage({
                id: `error_${Date.now()}`,
                role: 'system',
                content: 'Error: No hay sesión activa',
                timestamp: new Date(),
                isError: true
            });
            return;
        }
        
        // AGREGAR INMEDIATAMENTE el mensaje del usuario
        const userMessage = {
            id: messageId,
            role: 'user',
            content: message,
            timestamp: new Date(),
            status: 'sending'
        };
        
        console.log('👤 [sendMessage] Agregando mensaje de usuario:', userMessage);
        addMessage(userMessage);

        try {
            // Enviar al WebSocket
            const payload = {
                type: 'message',
                payload: {
                    content: message,
                    session_id: sessionIdentifier,
                    message_id: messageId
                }
            };
            
            console.log('📤 [sendMessage] Enviando payload:', payload);
            ws.current.send(JSON.stringify(payload));
            console.log('✅ [sendMessage] Mensaje enviado al WebSocket');
        } catch (error) {
            console.error('❌ Error sending message:', error);
            
            // Actualizar el mensaje a estado de error
            addMessage({
                id: messageId,
                role: 'user',
                content: message,
                timestamp: new Date(),
                status: 'error',
                error: error.message
            });
        }

        setInputMessage('');
        inputRef.current?.focus();
    };

    // Load chat history - CORREGIDO para evitar duplicados
// Load chat history - VERSIÓN CON MEJOR MANEJO DE DUPLICADOS
const loadChatHistory = useCallback(async (sessionUuid) => {
    try {
        console.log('📜 [loadChatHistory] Cargando historial para sesión:', sessionUuid);
        
        if (currentSession !== sessionUuid) {
            console.warn('⚠️ Saltando carga de historial - sesión ya no es la activa');
            return;
        }
        
        const response = await authenticatedFetch(
            `/api/n8n/chat/sessions/${sessionUuid}/messages?limit=50`
        );
        
        if (response.ok) {
            const data = await response.json();
            
            if (currentSession === sessionUuid && data.messages && Array.isArray(data.messages)) {
                // Usar Map para eliminar duplicados por ID
                const messageMap = new Map();
                
                // Primero agregar mensajes existentes (si los hay)
                messages.forEach(msg => {
                    if (msg.id) {
                        messageMap.set(msg.id, msg);
                    }
                });
                
                // Agregar mensajes del historial (en orden inverso para cronológico)
                for (let i = data.messages.length - 1; i >= 0; i--) {
                    const msg = data.messages[i];
                    const msgId = msg.message_uuid || msg.id;
                    
                    if (msgId && !messageMap.has(msgId)) {
                        messageMap.set(msgId, {
                            id: msgId,
                            role: msg.role || 'assistant',
                            content: msg.content,
                            timestamp: new Date(msg.sent_at || msg.timestamp),
                            metadata: msg.metadata
                        });
                    }
                }
                
                // Convertir Map a array
                const uniqueMessages = Array.from(messageMap.values());
                
                console.log(`✅ Cargados ${uniqueMessages.length} mensajes únicos (de ${data.messages.length} en historial)`);
                setMessages(uniqueMessages);
            }
        } else if (response.status === 404) {
            console.warn('⚠️ Session not found, starting fresh');
            if (currentSession === sessionUuid) {
                setMessages([]);
            }
        }
    } catch (error) {
        console.error('❌ Error loading chat history:', error);
    }
}, [currentSession, messages]); // Agrega messages como dependencia

    // Load user sessions
    const loadSessions = useCallback(async () => {
        if (hasLoadedSessions && sessions.length > 0) {
            console.log('🔄 Sesiones ya cargadas, omitiendo...');
            return;
        }
        
        try {
            setIsLoadingSessions(true);
            console.log('📋 Cargando sesiones...');
            
            const response = await authenticatedFetch('/api/n8n/chat/sessions');
            
            console.log('📋 Load sessions response:', response.status, response.statusText);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Sessions loaded:', data);
                
                if (data.sessions && Array.isArray(data.sessions)) {
                    const sortedSessions = data.sessions.sort((a, b) => 
                        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
                    );
                    
                    setSessions(sortedSessions);
                    setHasLoadedSessions(true);
                    
                    if (!currentSession && sortedSessions.length > 0) {
                        const mostRecent = sortedSessions[0];
                        const sessionUuid = mostRecent.session_uuid;
                        if (sessionUuid) {
                            console.log('🎯 Estableciendo sesión inicial (UUID):', sessionUuid);
                            setCurrentSession(sessionUuid);
                            localStorage.setItem('chat_session_id', sessionUuid);
                            setActiveSessionInfo(mostRecent);
                            
                            if (isOpen) {
                                connectWebSocket(sessionUuid);
                            }
                        } else {
                            console.error('❌ Sesión sin session_uuid:', mostRecent);
                        }
                    }
                    
                    if (currentSession) {
                        const activeSession = sortedSessions.find(s => 
                            s.session_uuid === currentSession
                        );
                        if (activeSession) {
                            setActiveSessionInfo(activeSession);
                        }
                    }
                }
            } else {
                console.error('❌ Error loading sessions:', response.statusText);
            }
        } catch (error) {
            console.error('❌ Error loading sessions:', error);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [currentSession, isOpen, hasLoadedSessions, connectWebSocket]);

    // Create new session
    const createNewSession = async (title = 'Nueva conversación') => {
        setIsCreatingSession(true);
        try {
            console.log('📝 [createNewSession] Creando nueva sesión con título:', title);
            
            const requestBody = {
                title: title,
                context: {
                    source: "chatbot",
                    created_at: new Date().toISOString(),
                    user_type: "employee"
                }
            };
            
            const response = await authenticatedFetch('/api/n8n/chat/sessions', {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Session created:', data);
                
                const newSessionUuid = data.session_uuid;
                if (!newSessionUuid) {
                    throw new Error('No session_uuid returned from server');
                }
                
                const newSession = {
                    id: data.id,
                    session_uuid: data.session_uuid,
                    title: data.title || title,
                    created_at: data.created_at || new Date().toISOString(),
                    updated_at: data.updated_at || new Date().toISOString(),
                    message_count: 0,
                    status: data.status || 'active'
                };
                
                // 1. Limpiar mensajes anteriores
                setMessages([]);
                
                // 2. Actualizar lista de sesiones
                setSessions(prev => [newSession, ...prev]);
                
                // 3. Establecer nueva sesión como activa (usando session_uuid)
                setCurrentSession(newSessionUuid);
                setActiveSessionInfo(newSession);
                localStorage.setItem('chat_session_id', newSessionUuid);
                
                // 4. Conectar WebSocket con nueva sesión
                if (isOpen) {
                    console.log('🔗 Conectando WebSocket a nueva sesión...');
                    connectWebSocket(newSessionUuid);
                }
                
                // 5. Cerrar modales
                setShowSessionsMenu(false);
                setShowNewSessionModal(false);
                setNewSessionTitle('');
                
                return newSessionUuid;
            } else {
                const errorText = await response.text();
                throw new Error(`Error creando sesión: ${errorText}`);
            }
        } catch (error) {
            console.error('❌ Error creating session:', error);
            
            addMessage({
                id: `error_${Date.now()}`,
                role: 'system',
                content: `Error al crear nueva conversación: ${error.message}`,
                timestamp: new Date(),
                isError: true
            });
            
            throw error;
        } finally {
            setIsCreatingSession(false);
        }
    };

    // Función para manejar la creación de sesión con título personalizado
    const handleCreateNewSession = async () => {
        const title = newSessionTitle.trim() || 'Nueva conversación';
        await createNewSession(title);
    };

    // Función para crear nueva sesión desde el estado vacío
    const createNewSessionFromEmpty = async () => {
        setShowNewSessionModal(true);
        setNewSessionTitle('');
    };

    // Cambiar a una sesión existente
    const switchToSession = async (session) => {
        const sessionUuid = session.session_uuid;
        if (!sessionUuid) {
            console.error('❌ No session_uuid found in session:', session);
            return;
        }
        
        console.log('🔄 [switchToSession] Cambiando a sesión (UUID):', sessionUuid, session.title);
        
        // 1. Limpiar mensajes actuales inmediatamente
        setMessages([]);
        
        // 2. Actualizar sesión actual (usando session_uuid)
        setCurrentSession(sessionUuid);
        setActiveSessionInfo(session);
        localStorage.setItem('chat_session_id', sessionUuid);
        
        // 3. Conectar WebSocket para la nueva sesión
        if (isOpen) {
            console.log('🔗 Conectando WebSocket para nueva sesión...');
            connectWebSocket(sessionUuid);
        }
        
        // 4. Cargar historial de la nueva sesión
        await loadChatHistory(sessionUuid);
        
        // 5. Cerrar menú de sesiones
        setShowSessionsMenu(false);
    };

    // Formatear fecha para mostrar
    const formatDate = (dateString) => {
        if (!dateString) return 'Recién';
        
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffTime = Math.abs(now - date);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays === 0) {
                return 'Hoy';
            } else if (diffDays === 1) {
                return 'Ayer';
            } else if (diffDays < 7) {
                return `Hace ${diffDays} días`;
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return `Hace ${weeks} semana${weeks > 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                });
            }
        } catch (error) {
            return 'Recién';
        }
    };

    // Effects PRINCIPAL - ACTUALIZADO con manejo de token
    useEffect(() => {
        if (isOpen) {
            console.log('🚀 Chatbot abierto, inicializando...');
            
            const initializeChat = async () => {
                // 1. Intentar obtener token explícito (opcional, las cookies funcionan automáticamente)
                let token = getAuthToken();
                
                // 2. Si no hay token explícito, intentar obtenerlo del servidor
                // Esto es solo para casos donde necesitemos el token explícitamente
                if (!token) {
                    token = await fetchAuthTokenFromServer();
                }
                
                // 3. Guardar token si se obtuvo (para futuras referencias)
                if (token) {
                    localStorage.setItem('auth_token_jwt', token);
                }
                
                // 4. Cargar sesiones (las cookies se envían automáticamente)
                if (!hasLoadedSessions) {
                    await loadSessions();
                }
                
                // 5. Conectar WebSocket (las cookies HttpOnly se envían automáticamente)
                if (currentSession) {
                    console.log('🔗 Conectando WebSocket para sesión existente (UUID):', currentSession);
                    connectWebSocket(currentSession);
                }
            };
            
            initializeChat();
            setHasUnread(false);
        } else {
            console.log('🔒 Chatbot cerrado, limpiando...');
            cleanupWebSocket();
        }
        
        return () => {
            cleanupWebSocket();
        };
    }, [isOpen, cleanupWebSocket, loadSessions, currentSession, connectWebSocket, hasLoadedSessions]);

    // Effect para manejar cambios en currentSession
    useEffect(() => {
        if (isOpen && currentSession && hasLoadedSessions) {
            console.log('🔄 [EFFECT] Sesión cambiada a (UUID):', currentSession);
            
            setMessages([]);
            
            connectWebSocket(currentSession);
        }
    }, [currentSession, isOpen, hasLoadedSessions, connectWebSocket]);

    // Efecto para scroll automático
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Efecto para focus en input
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // Efecto para limpiar duplicados cuando cambian los mensajes
    useEffect(() => {
        const checkForDuplicates = () => {
            const ids = new Set();
            const duplicates = [];
            
            messages.forEach((msg, index) => {
                if (ids.has(msg.id)) {
                    duplicates.push({ index, id: msg.id });
                } else {
                    ids.add(msg.id);
                }
            });
            
            if (duplicates.length > 0) {
                console.warn(`⚠️ Encontrados ${duplicates.length} mensajes duplicados:`, duplicates);
                
                // Filtrar duplicados, manteniendo solo la primera ocurrencia
                const uniqueMessages = messages.filter((msg, index) => {
                    const isFirstOccurrence = messages.findIndex(m => m.id === msg.id) === index;
                    if (!isFirstOccurrence) {
                        console.log(`🗑️ Eliminando duplicado en índice ${index}: ${msg.id}`);
                    }
                    return isFirstOccurrence;
                });
                
                if (uniqueMessages.length !== messages.length) {
                    console.log(`🔄 Actualizando mensajes: ${messages.length} → ${uniqueMessages.length}`);
                    setMessages(uniqueMessages);
                }
            }
        };
        
        if (messages.length > 0) {
            checkForDuplicates();
        }
    }, [messages]);

    // Regenerar respuesta para un mensaje
    const regenerateResponse = async (messageId) => {
        if (!currentSession || !messageId || isRegenerating) {
            console.warn('⚠️ No se puede regenerar: falta sesión o mensaje');
            return;
        }

        setIsRegenerating(true);
        try {
            console.log('🔄 Regenerando respuesta para mensaje:', messageId);
            
            const response = await authenticatedFetch(
                `/api/n8n/chat/sessions/${currentSession}/regenerate`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        message_id: messageId
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Respuesta regenerada:', data);
                
                // El backend retorna el nuevo mensaje de regeneración
                // que será procesado por n8n y llegará vía WebSocket
                if (data.new_message) {
                    // Agregar mensaje de regeneración (será actualizado cuando llegue la respuesta)
                    addMessage({
                        id: data.new_message.message_uuid || `regen_${Date.now()}`,
                        role: 'user',
                        content: data.new_message.content,
                        timestamp: new Date(data.new_message.sent_at),
                        status: 'sent',
                        metadata: {
                            ...data.new_message.metadata,
                            is_regeneration: true,
                            original_message_id: messageId
                        }
                    });
                    
                    // Mostrar indicador de que se está procesando
                    setIsTyping(true);
                } else {
                    // Si no viene new_message, mostrar mensaje de confirmación
                    addMessage({
                        id: `regen_info_${Date.now()}`,
                        role: 'system',
                        content: '🔄 Respuesta regenerada. El asistente está procesando tu solicitud...',
                        timestamp: new Date()
                    });
                    setIsTyping(true);
                }
            } else {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al regenerar respuesta');
            }
        } catch (error) {
            console.error('❌ Error regenerando respuesta:', error);
            addMessage({
                id: `error_${Date.now()}`,
                role: 'system',
                content: `Error al regenerar respuesta: ${error.message}`,
                timestamp: new Date(),
                isError: true
            });
        } finally {
            setIsRegenerating(false);
            setShowMessageActions(null);
        }
    };

    // Generar resumen de la conversación
    const generateSummary = async () => {
        if (!currentSession || isGeneratingSummary) {
            console.warn('⚠️ No se puede generar resumen: falta sesión');
            return;
        }

        setIsGeneratingSummary(true);
        try {
            console.log('📝 Generando resumen para sesión:', currentSession);
            
            const response = await authenticatedFetch(
                `/api/n8n/chat/sessions/${currentSession}/summarize`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        max_length: 200 // Longitud máxima del resumen
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Resumen generado:', data);
                
                // Agregar mensaje con el resumen
                addMessage({
                    id: `summary_${Date.now()}`,
                    role: 'assistant',
                    content: `📋 **Resumen de la conversación:**\n\n${data.summary || data.content || 'Resumen generado exitosamente'}`,
                    timestamp: new Date(),
                    metadata: {
                        is_summary: true,
                        ...data.metadata
                    }
                });
            } else {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al generar resumen');
            }
        } catch (error) {
            console.error('❌ Error generando resumen:', error);
            addMessage({
                id: `error_${Date.now()}`,
                role: 'system',
                content: `Error al generar resumen: ${error.message}`,
                timestamp: new Date(),
                isError: true
            });
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Formatear hora para mensajes
    const formatTime = (date) => {
        return date.toLocaleTimeString('es-ES', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Toggle debug panel
    const toggleDebug = () => {
        const newState = !showDebug;
        setShowDebug(newState);
        localStorage.setItem('debugHidden', !newState);
    };

    // Render message - CON LOGS DE DEPURACIÓN
    // Render message - CON MEJOR MANEJO DE KEYS Y ACCIONES
    const renderMessage = (message, index) => {
        // Usar una combinación de ID e índice para la key
        const messageKey = `${message.id}_${index}_${message.timestamp.getTime()}`;
        
        console.log('🖥️ [renderMessage] Renderizando:', messageKey, message.role, message.content.substring(0, 30));
        
        const isUser = message.role === 'user';
        const isSystem = message.role === 'system';
        const showActions = showMessageActions === message.id;
        
        if (isSystem) {
            return (
                <div className="message-container system" key={messageKey}>
                    <div className={`message-bubble ${message.isError ? 'error' : 'system'}`}>
                        <div className="message-content">
                            {message.content}
                        </div>
                        <div className="message-time">
                            {formatTime(message.timestamp)}
                        </div>
                    </div>
                </div>
            );
        }
        
        // Obtener el último mensaje de usuario antes de este mensaje para regeneración
        const canRegenerate = isUser && index > 0 && messages[index - 1]?.role === 'assistant';
        const isLastUserMessage = isUser && index === messages.length - 1;
        
        return (
            <div 
                className={`message-container ${isUser ? 'user' : 'assistant'}`} 
                key={messageKey}
                onMouseEnter={() => {
                    if (isUser && !message.metadata?.is_regeneration) {
                        setShowMessageActions(message.id);
                    }
                }}
                onMouseLeave={() => {
                    if (showActions) {
                        setShowMessageActions(null);
                    }
                }}
            >
                <div className={`message-bubble ${isUser ? 'user' : 'assistant'}`}>
                    <div className="message-content">
                        {message.content}
                    </div>
                    <div className="message-time">
                        {formatTime(message.timestamp)}
                        {message.status === 'sending' && ' • Enviando...'}
                        {message.status === 'error' && ' • Error al enviar'}
                        {message.metadata?.is_regeneration && ' • Regenerado'}
                    </div>
                    {/* Acciones del mensaje */}
                    {showActions && isUser && !message.metadata?.is_regeneration && (
                        <div className="message-actions">
                            <button
                                className="message-action-btn"
                                onClick={() => regenerateResponse(message.id)}
                                disabled={isRegenerating}
                                title="Regenerar respuesta"
                            >
                                🔄 Regenerar
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render menú de sesiones
    const renderSessionsMenu = () => {
        if (!showSessionsMenu) return null;

        return (
            <div className="chatbot-sessions-menu" ref={sessionsMenuRef}>
                <div className="sessions-menu-header">
                    <h4>💬 Conversaciones</h4>
                    <button 
                        className="sessions-close-btn"
                        onClick={() => setShowSessionsMenu(false)}
                        title="Cerrar"
                    >
                        ×
                    </button>
                </div>
                
                <div className="sessions-menu-content">
                    <button 
                        className="new-session-btn"
                        onClick={() => {
                            setShowSessionsMenu(false);
                            setShowNewSessionModal(true);
                            setNewSessionTitle('');
                        }}
                    >
                        <span>+</span> Nueva conversación
                    </button>
                    
                    {isLoadingSessions ? (
                        <div className="sessions-loading">
                            <div className="loading-spinner"></div>
                            <span>Cargando conversaciones...</span>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="no-sessions">
                            <span>No hay conversaciones anteriores</span>
                        </div>
                    ) : (
                        <div className="sessions-list">
                            {sessions.map(session => {
                                const sessionUuid = session.session_uuid;
                                if (!sessionUuid) {
                                    console.warn('⚠️ Sesión sin session_uuid:', session);
                                    return null;
                                }
                                
                                const isActive = sessionUuid === currentSession;
                                return (
                                    <div 
                                        key={sessionUuid}
                                        className={`session-item ${isActive ? 'active' : ''}`}
                                        onClick={() => !isActive && switchToSession(session)}
                                    >
                                        <div className="session-title">
                                            <span className="session-icon">💬</span>
                                            {session.title || 'Conversación sin título'}
                                            {isActive && <span className="active-badge">Activa</span>}
                                        </div>
                                        <div className="session-meta">
                                            <span className="session-date">
                                                {formatDate(session.updated_at || session.created_at)}
                                            </span>
                                            {session.message_count > 0 && (
                                                <span className="session-message-count">
                                                    {session.message_count} mensaje{session.message_count !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render modal para nueva sesión
    const renderNewSessionModal = () => {
        if (!showNewSessionModal) return null;

        return (
            <>
                <div className="modal-overlay" onClick={() => !isCreatingSession && setShowNewSessionModal(false)} />
                <div className="new-session-modal">
                    <h3>📝 Nueva conversación</h3>
                    <input
                        ref={newSessionInputRef}
                        type="text"
                        className="new-session-input"
                        value={newSessionTitle}
                        onChange={(e) => setNewSessionTitle(e.target.value)}
                        placeholder="Ingresa un título para la conversación"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newSessionTitle.trim() && !isCreatingSession) {
                                handleCreateNewSession();
                            }
                        }}
                        disabled={isCreatingSession}
                    />
                    <div className="new-session-modal-buttons">
                        <button 
                            className="new-session-modal-btn secondary"
                            onClick={() => setShowNewSessionModal(false)}
                            disabled={isCreatingSession}
                        >
                            Cancelar
                        </button>
                        <button 
                            className="new-session-modal-btn primary"
                            onClick={handleCreateNewSession}
                            disabled={!newSessionTitle.trim() || isCreatingSession}
                        >
                            {isCreatingSession ? 'Creando...' : 'Crear'}
                        </button>
                    </div>
                </div>
            </>
        );
    };

    // Render debug panel
    const renderDebugPanel = () => {
        return (
            <div className="debug-panel-fixed" ref={debugPanelRef}>
                <div className="debug-header">
                    <span>🔍 Debug Info</span>
                    <button 
                        className="debug-toggle-btn"
                        onClick={toggleDebug}
                        title={showDebug ? "Ocultar debug" : "Mostrar debug"}
                    >
                        {showDebug ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>
                {showDebug && (
                    <div className="debug-content">
                        <div className="debug-row">
                            <span className="debug-label">WebSocket:</span>
                            <span className={`debug-value ${isConnected ? 'success' : 'error'}`}>
                                {isConnected ? '✅ Conectado' : '❌ Desconectado'}
                            </span>
                        </div>
                        <div className="debug-row">
                            <span className="debug-label">Sesión (UUID):</span>
                            <span className="debug-value" title={currentSession || 'Ninguna'}>
                                {currentSession ? (currentSession.length > 10 ? currentSession.substring(0, 10) + '...' : currentSession) : 'Ninguna'}
                            </span>
                        </div>
                        <div className="debug-row">
                            <span className="debug-label">Mensajes:</span>
                            <span className="debug-value">{messages.length}</span>
                        </div>
                        <div className="debug-row">
                            <span className="debug-label">Sesiones:</span>
                            <span className="debug-value">{sessions.length}</span>
                        </div>
                        <div className="debug-row">
                            <span className="debug-label">Estado:</span>
                            <span className="debug-value">
                                {isTyping ? '⌨️ Escribiendo...' : '✅ Listo'}
                            </span>
                        </div>
                        <button 
                            className="debug-action-btn"
                            onClick={async () => {
                                console.log('=== DIAGNÓSTICO COMPLETO ===');
                                
                                // 1. Cookies
                                console.log('🍪 COOKIES:');
                                document.cookie.split(';').forEach((c, i) => {
                                    console.log(`  ${i}: ${c.trim()}`);
                                });
                                
                                // 2. localStorage
                                console.log('💾 LOCALSTORAGE:');
                                Object.keys(localStorage).forEach(key => {
                                    console.log(`  ${key}: ${localStorage.getItem(key)?.substring(0, 50)}...`);
                                });
                                
                                // 3. Verificar conexión WebSocket
                                console.log('🔗 WEBSOCKET:');
                                console.log('  Estado:', ws.current?.readyState);
                                console.log('  URL:', ws.current?.url);
                                console.log('  Conectado:', isConnected);
                                
                                // 4. Verificar sesión
                                console.log('💬 SESIÓN:');
                                console.log('  ID:', currentSession);
                                console.log('  Mensajes:', messages.length);
                                
                                // 5. Probar API
                                try {
                                    const test = await fetch('/n8n/chat/sessions');
                                    console.log('🌐 API TEST:', test.status);
                                } catch (e) {
                                    console.error('❌ API Test failed:', e);
                                }
                            }}
                        >
                            🔍 Diagnóstico Completo
                        </button>
                        <button 
                            className="debug-action-btn"
                            onClick={() => {
                                console.log('=== MENSAJES ACTUALES ===');
                                messages.forEach((msg, i) => {
                                    console.log(`${i}: ID=${msg.id}, Role=${msg.role}, Content=${msg.content.substring(0, 50)}...`);
                                });
                            }}
                        >
                            Ver Mensajes en Consola
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="chatbot-container">
            {/* Botón para abrir/cerrar chatbot */}
            <button 
                className={`chatbot-toggle ${hasUnread ? 'unread' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title={hasUnread ? 'Tienes mensajes nuevos' : 'Abrir chatbot'}
            >
                {isOpen ? '✕' : '💬'}
                {hasUnread && !isOpen && <span className="unread-badge"></span>}
            </button>
            
            {/* Panel de sesiones */}
            {renderSessionsMenu()}
            
            {/* Modal para nueva sesión */}
            {renderNewSessionModal()}
            
            {/* Ventana del chatbot */}
            {isOpen && (
                <div className="chatbot-window">
                    {/* Header con información de sesión */}
                    <div className="chatbot-header">
                        <div className="chatbot-title-section">
                            <div className="chatbot-title">
                                <span>🤖 Asistente Coexpan</span>
                                <div className="chatbot-status">
                                    <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
                                    <span>{isConnected ? 'Conectado' : 'Conectando...'}</span>
                                </div>
                            </div>
                            {activeSessionInfo && (
                                <div className="session-info">
                                    <button 
                                        className="sessions-toggle-btn"
                                        onClick={() => setShowSessionsMenu(!showSessionsMenu)}
                                        title="Ver conversaciones"
                                    >
                                        <span>📁</span>
                                        {activeSessionInfo.title || 'Conversación actual'}
                                    </button>
                                    {/* Botón para generar resumen */}
                                    {messages.length > 2 && (
                                        <button
                                            className="summary-btn"
                                            onClick={generateSummary}
                                            disabled={isGeneratingSummary}
                                            title="Generar resumen de la conversación"
                                        >
                                            {isGeneratingSummary ? '⏳' : '📋'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <button 
                            className="chatbot-close"
                            onClick={() => setIsOpen(false)}
                            title="Cerrar chat"
                        >
                            ×
                        </button>
                    </div>

                    {/* Área de mensajes */}
                    <div className="chatbot-messages">
                        {messages.length === 0 && !isTyping ? (
                            <div className="empty-state">
                                <div className="empty-state-icon">🤖</div>
                                <div className="empty-state-text">
                                    ¡Hola! Soy tu asistente virtual de Coexpan.
                                    ¿En qué puedo ayudarte hoy?
                                </div>
                                <div className="empty-state-actions">
                                    <button 
                                        className="action-btn"
                                        onClick={createNewSessionFromEmpty}
                                    >
                                        Iniciar nueva conversación
                                    </button>
                                    {sessions.length > 0 && (
                                        <button 
                                            className="action-btn secondary"
                                            onClick={() => setShowSessionsMenu(true)}
                                        >
                                            Ver conversaciones anteriores
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, index) => renderMessage(msg, index))}
                                {isTyping && (
                                    <div className="message-container">
                                        <div className="message-typing">
                                            <div className="typing-dots">
                                                <div className="typing-dot"></div>
                                                <div className="typing-dot"></div>
                                                <div className="typing-dot"></div>
                                            </div>
                                            <span>Escribiendo...</span>
                                        </div>
                                    </div>
                                )}
                                
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    {/* Panel de Debug FIJO */}
                    {renderDebugPanel()}

                    {/* Área de entrada */}
                    <div className="chatbot-input">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder="Escribe tu mensaje..."
                            disabled={!isConnected || isTyping}
                        />
                        <div className="input-actions">
                            <button 
                                className="sessions-btn"
                                onClick={() => setShowSessionsMenu(!showSessionsMenu)}
                                title="Conversaciones"
                            >
                                📁
                            </button>
                            {/* Botón para regenerar última respuesta */}
                            {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (
                                <button
                                    className="regenerate-btn"
                                    onClick={() => {
                                        const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
                                        if (lastUserMessage) {
                                            regenerateResponse(lastUserMessage.id);
                                        }
                                    }}
                                    disabled={isRegenerating || !isConnected}
                                    title="Regenerar última respuesta"
                                >
                                    {isRegenerating ? '⏳' : '🔄'}
                                </button>
                            )}
                            <button 
                                onClick={sendMessage}
                                disabled={!inputMessage.trim() || !isConnected || isTyping}
                                title="Enviar mensaje"
                                className="send-btn"
                            >
                                {isTyping ? '⏳' : '➤'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chatbot;